import React, { useState, useEffect, useCallback, Suspense } from "react";
import { Routes, Route, useParams } from "react-router-dom";
import { CssBaseline, ThemeProvider, CircularProgress, Box } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import "moment/locale/en-gb";
import "moment/locale/zh-cn";
import moment from "moment-timezone";

import { logout, updatePreferenceByUser } from "./slices/auth";
import { clearMessage } from "slices/message";
import Layout from "./scenes/global/layout";
import { getRuntimeValue } from "config/runtime";
import publicRoutes from "routes/publicRoutes";
import appRoutes from "routes/appRoutes";
import adminRoutes from "routes/adminRoutes";

const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
);

function RedirectToNewPath() {
  const { id } = useParams();
  const attachmentUrl = getRuntimeValue("REACT_APP_ATTACHMENT_URL", "");

  useEffect(() => {
    const newUrl = `${attachmentUrl}${id}`;
    window.location.href = newUrl;
  }, [id, attachmentUrl]);

  return null;
}

function App() {
  const [theme, colorMode] = useMode();
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(true);
  const [menuArea, setMenuArea] = useState("HOME");
  const [locale, setLocale] = React.useState("en-gb");

  const { user: currentUser } = useSelector((state) => state.auth);

  const initialCopilotMode = useSelector((state) => {
    const preference = state.auth.user?.preferences?.find(
      (preference) => preference.name === "copilotOpen"
    )?.preferenceValue;

    return preference ? preference === "true" : false;
  });

  const initialCopilotSize = useSelector((state) => {
    const preference = state.auth.user?.preferences?.find(
      (preference) => preference.name === "copilotSize"
    )?.preferenceValue;

    return preference || "25";
  });

  const [isCopilotOpen, setIsCopilotOpen] = useState(initialCopilotMode);
  const [copilotSize, setCopilotSize] = useState(initialCopilotSize);

  const dispatch = useDispatch();

  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  if (moment.locale() !== locale) {
    moment.locale(locale);
  }

  const handelSetCopilotMode = (mode) => {
    dispatch(clearMessage());

    const preference = {
      preference_value: mode ? "true" : "false",
      name: "copilotOpen",
      username: currentUser.user.username,
    };

    dispatch(updatePreferenceByUser({ preference }));
    setIsCopilotOpen(mode);
  };

  const handelSetCopilotSize = (size) => {
    dispatch(clearMessage());

    const preference = {
      preference_value: size,
      name: "copilotSize",
      username: currentUser.user.username,
    };

    dispatch(updatePreferenceByUser({ preference }));
    setCopilotSize(size);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      dateLibInstance={moment}
      adapterLocale={locale}
    >
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />

          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route
                element={
                  <Layout
                    copilotSize={copilotSize}
                    setCopilotSize={handelSetCopilotSize}
                    setIsCopilotOpen={handelSetCopilotMode}
                    isCopilotOpen={isCopilotOpen}
                    setIsNavMenuOpen={setIsNavMenuOpen}
                    isNavMenuOpen={isNavMenuOpen}
                    menuArea={menuArea}
                  />
                }
              >
                <Route path="/attachment/file/:id" element={<RedirectToNewPath />} />
                {publicRoutes}
                {appRoutes}
                {adminRoutes}
              </Route>
            </Routes>
          </Suspense>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </LocalizationProvider>
  );
}

export default App;
